import React from "react";
import { reloadOnceForStaleChunk } from "../../utils/staleChunkReload";

const hasArrayChanged = (a = [], b = []) => {
  if (a.length !== b.length) {
    return true;
  }

  return a.some((item, index) => !Object.is(item, b[index]));
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    if (reloadOnceForStaleChunk(error)) return;

    if (typeof this.props.onError === "function") {
      this.props.onError(error, errorInfo);
      return;
    }

    // Keep a default log for local debugging when no custom reporter is provided.
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (!this.state.hasError) {
      return;
    }

    if (!hasArrayChanged(prevProps.resetKeys, this.props.resetKeys)) {
      return;
    }

    if (typeof this.props.onReset === "function") {
      this.props.onReset({
        reason: "keys",
        prevResetKeys: prevProps.resetKeys,
        resetKeys: this.props.resetKeys,
      });
    }

    this.setState({
      hasError: false,
      error: null,
    });
  }

  resetErrorBoundary = (...args) => {
    if (typeof this.props.onReset === "function") {
      this.props.onReset({
        reason: "imperative",
        args,
      });
    }

    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { fallback, children } = this.props;

    if (!hasError) {
      return children;
    }

    if (typeof fallback === "function") {
      return fallback({
        error,
        resetErrorBoundary: this.resetErrorBoundary,
      });
    }

    if (fallback) {
      return fallback;
    }

    return null;
  }
}

export default ErrorBoundary;
